import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { usePartner } from '@/contexts/PartnerContext';
import {
  partnerApi,
  type DashboardData,
  type Purchase,
  type PurchasesListData,
  type StatsData,
  type ProfileData,
  type BalanceData,
} from '@/lib/partnerApi';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  LayoutDashboard, History, BarChart2, UserCircle, Wallet,
  LogOut, RefreshCw, Menu, X, Search, ExternalLink, HelpCircle,
  TrendingUp, Lock, DollarSign, Activity, CheckCircle2, AlertTriangle,
  ChevronLeft, ChevronRight, Info,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend, BarChart, Bar,
} from 'recharts';

// ─── helpers ────────────────────────────────────────────────────────────────

function fmtUsd(v: number | null | undefined, digits = 2) {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return `$${v.toFixed(digits)}`;
}

function fmtDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function fmtUnix(value: unknown) {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return fmtDate(new Date(n * 1000).toISOString());
}

// Поля реквеста Xpanda в том порядке, в каком их показываем в карточке. Всё,
// чего тут нет, выводится следом как есть — если Xpanda добавит поле, оно
// появится в карточке само, без правок на нашей стороне.
const RAW_LABELS: Record<string, string> = {
  id: 'ID реквеста',
  custom_id: 'Custom ID',
  product: 'Товар',
  status: 'Статус',
  offer_id: 'Offer ID',
  max_price: 'Максимальная цена',
  partner: 'Steam ID получателя',
  token: 'Trade-токен',
  error_code: 'Код ошибки',
  created_at: 'Создан',
  updated_at: 'Обновлён',
};

const RAW_ORDER_LABELS: Record<string, string> = {
  status: 'Статус ордера',
  exec_price: 'Цена исполнения',
  steam_trade_offer_id: 'Steam trade offer',
  steam_trade_offer_expires_at: 'Оффер истекает',
  steam_trade_settle_at: 'Расчёт по трейду',
};

function fmtRawValue(key: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'да' : 'нет';
  // Цены Xpanda отдаёт в тысячных долях доллара: 484 → $0.484.
  if (key === 'max_price' || key === 'exec_price') {
    const n = Number(value);
    return Number.isFinite(n) ? fmtUsd(n / 1000, 3) : String(value);
  }
  if (key.endsWith('_at')) return fmtUnix(value);
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function fmtDay(iso: string) {
  try {
    return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
  } catch {
    return iso;
  }
}

function StatusBadge({ color, label }: { color: string; label: string }) {
  const colors: Record<string, string> = {
    green: 'bg-green-500/15 text-green-400 border-green-500/30',
    yellow: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
    blue: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
    red: 'bg-red-500/15 text-red-400 border-red-500/30',
    gray: 'bg-gray-500/15 text-gray-400 border-gray-500/30',
  };
  return (
    <span className={`inline-flex text-xs font-semibold px-2 py-0.5 rounded border ${colors[color] || colors.gray}`}>
      {label}
    </span>
  );
}

function KpiCard({
  icon: Icon, label, value, sub, accent = 'blue',
}: {
  icon: React.ElementType; label: string; value: string; sub?: string;
  accent?: 'blue' | 'green' | 'yellow' | 'red' | 'purple';
}) {
  const tones: Record<string, string> = {
    blue: 'bg-blue-500/10 text-blue-400',
    green: 'bg-green-500/10 text-green-400',
    yellow: 'bg-yellow-500/10 text-yellow-400',
    red: 'bg-red-500/10 text-red-400',
    purple: 'bg-purple-500/10 text-purple-400',
  };
  return (
    <div className="bg-[#161b27] border border-[#252d42] rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{label}</span>
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${tones[accent]}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="text-xl font-bold text-white">{value}</div>
      {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  // Mobile: вертикальный стек (label сверху, value снизу), desktop: горизонтально.
  // Без этого w-32 для лейбла на 320px-экране съедает треть ширины и значения
  // переносятся в одну колонку.
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-3">
      <span className="sm:w-32 sm:shrink-0 text-[10px] sm:text-xs uppercase sm:normal-case tracking-wider sm:tracking-normal text-gray-500 sm:pt-0.5">
        {label}
      </span>
      <div className="flex-1 min-w-0 break-words">{children}</div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Dashboard
// ═════════════════════════════════════════════════════════════════════════════

// Кнопка «обновить незавершённые»: тянет из Xpanda все реквесты, которые ещё
// не в терминальном статусе. Фоновый поллер делает то же самое по кругу, но
// партнёру нужно уметь обновить всё разом, не дожидаясь очереди.
function useRefreshUnfinished(onDone?: () => void | Promise<void>) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const run = async () => {
    setBusy(true);
    setNote(null);
    try {
      const r = await partnerApi.refreshUnfinished();
      setNote(
        r.checked === 0
          ? 'Незавершённых реквестов нет'
          : `Проверено ${r.checked}, обновлено ${r.updated}`
            + (r.failed ? `, не ответили ${r.failed}` : '')
            + (r.timed_out ? `, не успели ${r.timed_out}` : '')
            + `. Осталось незавершённых: ${r.still_unfinished}`,
      );
      await onDone?.();
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'Не удалось обновить');
    } finally {
      setBusy(false);
    }
  };

  return { busy, note, run };
}

function RefreshNote({ note }: { note: string | null }) {
  if (!note) return null;
  return (
    <div className="text-xs text-gray-400 bg-white/5 border border-white/10 rounded-lg px-4 py-2">
      {note}
    </div>
  );
}

function PartnerDashboard() {
  const { refreshBalance } = usePartner();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [detail, setDetail] = useState<Purchase | null>(null);

  const load = async () => {
    try {
      const d = await partnerApi.getDashboard();
      setData(d);
      return d;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 20;       // 20 × 3s = 60 секунд
    const POLL_DELAY = 3000;

    const isEmpty = (d: DashboardData | null | undefined) =>
      !!d &&
      d.kpis.active_count === 0 &&
      d.kpis.done_count_30d === 0 &&
      d.kpis.error_count_30d === 0;

    const poll = async () => {
      const d = await load();
      if (cancelled) return;
      if (isEmpty(d) && attempts < MAX_ATTEMPTS) {
        attempts += 1;
        setSyncing(true);
        timer = setTimeout(poll, POLL_DELAY);
      } else {
        setSyncing(false);
      }
    };

    poll();
    return () => { cancelled = true; clearTimeout(timer); };
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([load(), refreshBalance()]).catch(() => {});
    setRefreshing(false);
    setSyncing(false);
  };

  const unfinished = useRefreshUnfinished(async () => {
    await Promise.all([load(), refreshBalance()]).catch(() => {});
    setSyncing(false);
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white">Dashboard</h2>
          <p className="text-sm text-gray-500">Баланс + KPI за последние 30 дней</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
          <Button onClick={unfinished.run} disabled={unfinished.busy} size="sm"
            className="bg-blue-500/15 border border-blue-500/40 text-blue-200 hover:bg-blue-500/25">
            <RefreshCw className={`w-4 h-4 mr-2 ${unfinished.busy ? 'animate-spin' : ''}`} />
            Обновить незавершённые
          </Button>
          <Button onClick={handleRefresh} disabled={refreshing} size="sm"
            className="bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10">
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Обновить
          </Button>
        </div>
      </div>

      {/* 6 KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <KpiCard icon={DollarSign} label="Общий баланс" value={fmtUsd(data?.balance.total_usd)} accent="blue" />
        <KpiCard icon={Lock} label="Заблокировано" value={fmtUsd(data?.balance.locked_usd)} accent="yellow" />
        <KpiCard icon={TrendingUp} label="Доступно" value={fmtUsd(data?.balance.available_usd)} accent="green" />
        <KpiCard icon={Activity} label="Активные" value={String(data?.kpis.active_count ?? 0)}
          sub="MONEY_BLOCKED + IN_PROGRESS" accent="purple" />
        <KpiCard icon={CheckCircle2} label="Done (30д)" value={String(data?.kpis.done_count_30d ?? 0)}
          sub={data?.kpis.success_rate_30d !== null && data?.kpis.success_rate_30d !== undefined
            ? `Успех: ${data.kpis.success_rate_30d}%`
            : undefined}
          accent="green" />
      </div>

      {loading && !data && <div className="text-center text-gray-500 py-8">Загрузка…</div>}

      {syncing && (
        <div className="flex items-center gap-2 text-sm text-yellow-400 bg-yellow-500/10 border border-yellow-500/20 rounded-lg px-4 py-2">
          <RefreshCw className="w-4 h-4 animate-spin shrink-0" />
          Синхронизируем данные с Xpanda… Обновляем автоматически через несколько секунд.
        </div>
      )}

      <RefreshNote note={unfinished.note} />

      {/* Charts */}
      {data && (
        <div className="grid grid-cols-1 gap-4">
          <div className="bg-[#161b27] border border-[#252d42] rounded-xl p-4">
            <div className="text-sm font-semibold mb-3">Объём покупок (30 дней)</div>
            <div className="h-64">
              <ResponsiveContainer>
                <LineChart data={data.volume_by_day}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#252d42" />
                  <XAxis dataKey="date" tickFormatter={fmtDay} stroke="#6b7280" fontSize={11} />
                  <YAxis stroke="#6b7280" fontSize={11} />
                  <Tooltip contentStyle={{ background: '#0f1117', border: '1px solid #252d42', color: '#e5e7eb' }}
                    labelFormatter={fmtDay} />
                  <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} dot={false} name="Покупок" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Recent active — на mobile стек карточек, на ≥sm горизонтальная таблица */}
      {data && data.recent_active.length > 0 && (
        <div className="bg-[#161b27] border border-[#252d42] rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-[#252d42]">
            <div className="text-sm font-semibold">Активные покупки</div>
            <div className="text-xs text-gray-500">Последние 10 в MONEY_BLOCKED / IN_PROGRESS</div>
          </div>
          {/* Mobile cards */}
          <div className="sm:hidden divide-y divide-[#252d42]">
            {data.recent_active.map((p) => (
              <button key={p.id} onClick={() => setDetail(p)}
                className="w-full text-left px-4 py-3 hover:bg-white/5 active:bg-white/10 space-y-1">
                <div className="flex items-start justify-between gap-2">
                  <span className="font-mono text-xs text-white break-all">{p.product || '—'}</span>
                  <StatusBadge color={p.purchase_status_color} label={p.purchase_status_label} />
                </div>
                <div className="font-mono text-[11px] text-gray-400 break-all">{p.custom_id || '—'}</div>
                <div className="flex items-center justify-between text-[11px] text-gray-500">
                  <span>{fmtDate(p.created_at)}</span>
                  <span className="text-white">{fmtUsd(p.max_price_usd)}</span>
                </div>
              </button>
            ))}
          </div>
          {/* Desktop table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-xs min-w-[640px]">
              <thead className="text-[10px] uppercase tracking-wider text-gray-500 bg-[#0f1117]">
                <tr>
                  <th className="text-left px-4 py-2">Товар</th>
                  <th className="text-left px-4 py-2">Custom ID</th>
                  <th className="text-left px-4 py-2">Статус</th>
                  <th className="text-right px-4 py-2">Max</th>
                  <th className="text-left px-4 py-2">Создана</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_active.map((p) => (
                  <tr key={p.id} onClick={() => setDetail(p)}
                    className="border-t border-[#252d42] hover:bg-white/5 cursor-pointer">
                    <td className="px-4 py-2 font-mono">{p.product || '—'}</td>
                    <td className="px-4 py-2 font-mono text-gray-400">{p.custom_id || '—'}</td>
                    <td className="px-4 py-2"><StatusBadge color={p.purchase_status_color} label={p.purchase_status_label} /></td>
                    <td className="px-4 py-2 text-right">{fmtUsd(p.max_price_usd)}</td>
                    <td className="px-4 py-2 text-gray-400">{fmtDate(p.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent events (completed/errors/returns) — то же: cards на mobile, table на desktop */}
      {data && data.recent_events && data.recent_events.length > 0 && (
        <div className="bg-[#161b27] border border-[#252d42] rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-[#252d42]">
            <div className="text-sm font-semibold">Последние события</div>
            <div className="text-xs text-gray-500">Завершённые сделки, возвраты, ошибки</div>
          </div>
          <div className="sm:hidden divide-y divide-[#252d42]">
            {data.recent_events.map((p) => (
              <button key={p.id} onClick={() => setDetail(p)}
                className="w-full text-left px-4 py-3 hover:bg-white/5 active:bg-white/10 space-y-1">
                <div className="flex items-start justify-between gap-2">
                  <span className="font-mono text-xs text-white break-all">{p.product || '—'}</span>
                  <StatusBadge color={p.purchase_status_color} label={p.purchase_status_label} />
                </div>
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-red-400">{p.active_order?.error_code || ''}</span>
                  <span className="text-white">{fmtUsd(p.exec_price_usd ?? null)}</span>
                </div>
                <div className="text-[11px] text-gray-500">{fmtDate(p.updated_at)}</div>
              </button>
            ))}
          </div>
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-xs min-w-[640px]">
              <thead className="text-[10px] uppercase tracking-wider text-gray-500 bg-[#0f1117]">
                <tr>
                  <th className="text-left px-4 py-2">Товар</th>
                  <th className="text-left px-4 py-2">Статус</th>
                  <th className="text-right px-4 py-2">Exec</th>
                  <th className="text-left px-4 py-2">Ошибка</th>
                  <th className="text-left px-4 py-2">Обновлена</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_events.map((p) => (
                  <tr key={p.id} onClick={() => setDetail(p)}
                    className="border-t border-[#252d42] hover:bg-white/5 cursor-pointer">
                    <td className="px-4 py-2 font-mono">{p.product || '—'}</td>
                    <td className="px-4 py-2"><StatusBadge color={p.purchase_status_color} label={p.purchase_status_label} /></td>
                    <td className="px-4 py-2 text-right">{fmtUsd(p.exec_price_usd ?? null)}</td>
                    <td className="px-4 py-2 text-red-400 text-xs">{p.active_order?.error_code || '—'}</td>
                    <td className="px-4 py-2 text-gray-400">{fmtDate(p.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <PurchaseDetailDialog purchase={detail} onClose={() => { setDetail(null); load(); }} />
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Balance (standalone section)
// ═════════════════════════════════════════════════════════════════════════════

function PartnerBalanceSection() {
  const [data, setData] = useState<BalanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const d = await partnerApi.getBalance();
      setData(d);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await load().catch(() => {});
    setRefreshing(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white">Баланс</h2>
          <p className="text-sm text-gray-500">Структура средств партнёра</p>
        </div>
        <Button onClick={handleRefresh} disabled={refreshing} size="sm"
          className="bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 self-start sm:self-auto">
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Обновить
        </Button>
      </div>

      {loading && !data && <div className="text-center text-gray-500 py-8">Загрузка…</div>}

      {data && (
        <>
          {/* Main balance cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-[#161b27] border border-[#252d42] rounded-xl p-5">
              <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Total Balance</div>
              <div className="text-2xl font-bold text-white">{fmtUsd(data.total_usd)}</div>
            </div>
            <div className="bg-[#161b27] border border-yellow-500/20 rounded-xl p-5">
              <div className="text-[10px] font-bold uppercase tracking-wider text-yellow-500 mb-1">Locked</div>
              <div className="text-2xl font-bold text-yellow-400">{fmtUsd(data.locked_usd)}</div>
            </div>
            <div className="bg-[#161b27] border border-green-500/20 rounded-xl p-5">
              <div className="text-[10px] font-bold uppercase tracking-wider text-green-500 mb-1">Available</div>
              <div className="text-2xl font-bold text-green-400">{fmtUsd(data.available_usd)}</div>
            </div>
          </div>

          {/* Formula + details */}
          <div className="bg-[#161b27] border border-[#252d42] rounded-xl p-5 space-y-4">
            <div className="flex items-center gap-2 text-sm">
              <Info className="w-4 h-4 text-blue-400 shrink-0" />
              <span className="text-gray-400">
                Формула: <code className="text-blue-400 bg-blue-500/10 px-1.5 py-0.5 rounded">available = total - locked</code>
              </span>
            </div>
            <div className="border-t border-[#252d42] pt-4 space-y-3">
              <Row label="Total">
                <span className="text-white font-bold">{fmtUsd(data.total_usd)}</span>
              </Row>
              <Row label="Locked (Xpanda)">
                <span className="text-yellow-400 font-bold">{fmtUsd(data.locked_usd)}</span>
                <span className="text-gray-500 text-xs ml-2">заблокировано на стороне Xpanda</span>
              </Row>
              <Row label="В активных покупках">
                <span className="text-purple-400 font-bold">{fmtUsd(data.active_locked_usd)}</span>
                <span className="text-gray-500 text-xs ml-2">сумма max_price по MONEY_BLOCKED + IN_PROGRESS</span>
              </Row>
              <Row label="Available">
                <span className="text-green-400 font-bold">{fmtUsd(data.available_usd)}</span>
                <span className="text-gray-500 text-xs ml-2">доступно для новых операций</span>
              </Row>
            </div>
          </div>

          <div className="bg-blue-500/5 border border-blue-500/15 rounded-xl p-4 flex items-start gap-3">
            <Info className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
            <div className="text-sm text-gray-400">
              <p className="text-blue-400 font-semibold mb-1">Как работает баланс</p>
              <p>При создании реквеста на покупку средства блокируются (locked). После успешной покупки списывается фактическая цена (exec_price). При ошибке средства возвращаются на available.</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Purchase Detail Dialog (общий для Dashboard и Purchases)
// ═════════════════════════════════════════════════════════════════════════════

function PurchaseDetailDialog({ purchase, onClose }: { purchase: Purchase | null; onClose: () => void }) {
  // Итог запроса помечаем ключом «id + номер обновления»: пока ключ не совпал,
  // запрос считается идущим. Так состояние загрузки вычисляется, а не хранится
  // — иначе пришлось бы звать setState прямо в теле эффекта, а это каскад
  // рендеров. Последний успешный ответ лежит отдельно и переживает повторное
  // обновление, поэтому блок с реквестом при нажатии «Обновить» не мигает.
  const [result, setResult] = useState<{ key: string; error?: string } | null>(null);
  const [last, setLast] = useState<{ id: string; data: Purchase } | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const id = purchase?.id ?? null;
  const key = id ? `${id}:${reloadKey}` : null;

  // Открыли карточку — сразу тянем реквест из Xpanda. В списке лежит локальный
  // снапшот, а тут нужны поля, которых в нашей таблице нет вовсе: offer_id,
  // Steam ID получателя, trade-токен, сроки по трейд-офферу.
  useEffect(() => {
    if (!id || !key) return;
    let cancelled = false;
    partnerApi.getPurchaseDetail(id, true)
      .then((d) => {
        if (cancelled) return;
        setLast({ id, data: d });
        setResult({ key });
      })
      .catch((e) => {
        if (cancelled) return;
        setResult({
          key,
          error: e instanceof Error ? e.message : 'Не удалось получить данные из Xpanda',
        });
      });
    return () => { cancelled = true; };
  }, [id, key]);

  const current = result && result.key === key ? result : null;
  const loading = !!id && current === null;
  const error = current?.error ?? null;
  // Привязка к id: переключились на другую покупку — данные предыдущей не покажем.
  const detail = last && last.id === id ? last.data : null;

  // Пока Xpanda не ответил, показываем то, что пришло со списком — карточка
  // не должна открываться пустой.
  const p = detail ?? purchase;
  const raw = (detail?.raw ?? null) as Record<string, unknown> | null;
  const rawOrder = (raw?.active_order ?? null) as Record<string, unknown> | null;
  const extraKeys = raw
    ? Object.keys(raw).filter((k) => k !== 'active_order' && !(k in RAW_LABELS))
    : [];

  return (
    <Dialog open={!!purchase} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#161b27] border-[#252d42] text-white max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3 pr-6">
            <DialogTitle>Детали покупки</DialogTitle>
            <Button onClick={() => setReloadKey((k) => k + 1)} disabled={loading} size="sm"
              className="bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 shrink-0">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Обновить
            </Button>
          </div>
        </DialogHeader>
        {p && (
          <div className="space-y-3 text-sm">
            <Row label="ID"><span className="font-mono text-xs">{p.id}</span></Row>
            <Row label="Custom ID">{p.custom_id || '—'}</Row>
            <Row label="Товар">{p.product || '—'}</Row>
            <Row label="Статус"><StatusBadge color={p.purchase_status_color} label={p.purchase_status_label} /></Row>
            <Row label="Max">{fmtUsd(p.max_price_usd)}</Row>
            <Row label="Exec">{fmtUsd(p.exec_price_usd ?? null)}</Row>
            {p.active_order && (
              <>
                <Row label="Order status">{p.active_order.status_label || p.active_order.status}</Row>
                {p.active_order.steam_trade_offer_id && (
                  <Row label="Steam trade">
                    <span className="font-mono text-xs">{p.active_order.steam_trade_offer_id}</span>
                  </Row>
                )}
                {p.active_order.error_code && (
                  <Row label="Error code"><span className="text-red-400">{p.active_order.error_code}</span></Row>
                )}
                {/* Error description with actionable hint */}
                {p.active_order.error_info && (
                  <div className={`p-3 rounded-lg border text-sm ${
                    p.active_order.error_info.type === 'user_action'
                      ? 'bg-orange-500/10 border-orange-500/25'
                      : 'bg-red-500/10 border-red-500/25'
                  }`}>
                    <div className="flex items-center gap-2 mb-1">
                      <AlertTriangle className={`w-4 h-4 ${
                        p.active_order.error_info.type === 'user_action' ? 'text-orange-400' : 'text-red-400'
                      }`} />
                      <span className={`font-semibold ${
                        p.active_order.error_info.type === 'user_action' ? 'text-orange-400' : 'text-red-400'
                      }`}>
                        {p.active_order.error_info.label}
                      </span>
                      {p.active_order.error_info.type === 'user_action' && (
                        <span className="text-[10px] uppercase font-bold text-orange-500 bg-orange-500/20 px-1.5 py-0.5 rounded">
                          Требуется действие
                        </span>
                      )}
                    </div>
                    <p className="text-gray-400 text-xs">{p.active_order.error_info.hint}</p>
                  </div>
                )}
              </>
            )}
            <Row label="Создана">{fmtDate(p.created_at)}</Row>
            <Row label="Обновлена">{fmtDate(p.updated_at)}</Row>
            {p.last_sync_at && <Row label="Последний sync">{fmtDate(p.last_sync_at)}</Row>}

            {loading && !raw && (
              <div className="flex items-center gap-2 text-xs text-gray-500 pt-1">
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                Запрашиваем реквест в Xpanda…
              </div>
            )}

            {error && (
              <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/25 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            {raw && (
              <div className="pt-3 border-t border-[#252d42] space-y-3">
                <div className="text-xs font-bold uppercase tracking-wider text-gray-500">
                  Реквест в Xpanda
                </div>
                {Object.keys(RAW_LABELS).filter((k) => k in raw).map((k) => (
                  <Row key={k} label={RAW_LABELS[k]}>
                    <span className="font-mono text-xs break-all">{fmtRawValue(k, raw[k])}</span>
                  </Row>
                ))}
                {extraKeys.map((k) => (
                  <Row key={k} label={k}>
                    <span className="font-mono text-xs break-all">{fmtRawValue(k, raw[k])}</span>
                  </Row>
                ))}

                {rawOrder && Object.keys(rawOrder).length > 0 && (
                  <>
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-500 pt-1">
                      Активный ордер
                    </div>
                    {Object.keys(rawOrder).map((k) => (
                      <Row key={k} label={RAW_ORDER_LABELS[k] ?? k}>
                        <span className="font-mono text-xs break-all">{fmtRawValue(k, rawOrder[k])}</span>
                      </Row>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
// ═════════════════════════════════════════════════════════════════════════════
// Purchases (list + filters + pagination)
// ═════════════════════════════════════════════════════════════════════════════

const PURCHASE_STATUS_FILTERS = [
  { code: 'MONEY_BLOCKED', label: 'Блок.', color: 'yellow' },
  { code: 'IN_PROGRESS', label: 'В обр.', color: 'blue' },
  { code: 'DONE', label: 'Done', color: 'green' },
  { code: 'ERROR', label: 'Error', color: 'red' },
];

const ORDER_STATUS_FILTERS = [
  { code: 'PENDING', label: 'Pending' },
  { code: 'IN_PROGRESS', label: 'In Progress' },
  { code: 'BOUGHT', label: 'Bought' },
  { code: 'WAITING_FOR_TRADE', label: 'Wait Trade' },
  { code: 'TRADE', label: 'Trade' },
  { code: 'DONE', label: 'Done' },
  { code: 'ERROR', label: 'Error' },
  { code: 'RETURNED', label: 'Returned' },
];

function PartnerPurchases() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [orderStatuses, setOrderStatuses] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [data, setData] = useState<PurchasesListData | null>(null);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<Purchase | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const unfinished = useRefreshUnfinished(() => { setReloadKey((k) => k + 1); });

  // Debounce поиска на 300 мс
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await partnerApi.getPurchases({
          page, page_size: pageSize,
          status: statuses.length ? statuses : undefined,
          order_status: orderStatuses.length ? orderStatuses : undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          search: search || undefined,
        });
        if (!cancelled) setData(res);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [page, pageSize, statuses, orderStatuses, dateFrom, dateTo, search, reloadKey]);

  const toggleStatus = (code: string) => {
    setStatuses((prev) => prev.includes(code) ? prev.filter((s) => s !== code) : [...prev, code]);
    setPage(1);
  };

  const toggleOrderStatus = (code: string) => {
    setOrderStatuses((prev) => prev.includes(code) ? prev.filter((s) => s !== code) : [...prev, code]);
    setPage(1);
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white">История покупок</h2>
          <p className="text-sm text-gray-500">Данные из локальной БД, синкаются с Xpanda каждые 45 сек</p>
        </div>
        <Button onClick={unfinished.run} disabled={unfinished.busy} size="sm"
          className="bg-blue-500/15 border border-blue-500/40 text-blue-200 hover:bg-blue-500/25 self-start sm:self-auto">
          <RefreshCw className={`w-4 h-4 mr-2 ${unfinished.busy ? 'animate-spin' : ''}`} />
          Обновить незавершённые
        </Button>
      </div>

      <RefreshNote note={unfinished.note} />

      {/* Filters — на mobile лейбл сверху, кнопки/инпуты пол-ширины */}
      <div className="bg-[#161b27] border border-[#252d42] rounded-xl p-3 sm:p-4 space-y-3">
        {/* Purchase status */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:flex-wrap gap-2">
          <span className="text-[10px] uppercase tracking-wider text-gray-500 sm:mr-2 sm:w-20 sm:shrink-0">Purchase</span>
          <div className="flex flex-wrap gap-2">
            {PURCHASE_STATUS_FILTERS.map((s) => {
              const active = statuses.includes(s.code);
              return (
                <button key={s.code} onClick={() => toggleStatus(s.code)}
                  className={`text-xs px-3 py-1.5 rounded border transition-colors ${
                    active
                      ? 'bg-blue-500/20 text-blue-300 border-blue-500/50'
                      : 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10'
                  }`}>
                  {s.label}
                </button>
              );
            })}
            {statuses.length > 0 && (
              <button onClick={() => { setStatuses([]); setPage(1); }}
                className="text-xs text-gray-500 hover:text-white px-1">
                Сбросить
              </button>
            )}
          </div>
        </div>

        {/* Order status */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:flex-wrap gap-2">
          <span className="text-[10px] uppercase tracking-wider text-gray-500 sm:mr-2 sm:w-20 sm:shrink-0">Order</span>
          <div className="flex flex-wrap gap-2">
            {ORDER_STATUS_FILTERS.map((s) => {
              const active = orderStatuses.includes(s.code);
              return (
                <button key={s.code} onClick={() => toggleOrderStatus(s.code)}
                  className={`text-xs px-3 py-1.5 rounded border transition-colors ${
                    active
                      ? 'bg-purple-500/20 text-purple-300 border-purple-500/50'
                      : 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10'
                  }`}>
                  {s.label}
                </button>
              );
            })}
            {orderStatuses.length > 0 && (
              <button onClick={() => { setOrderStatuses([]); setPage(1); }}
                className="text-xs text-gray-500 hover:text-white px-1">
                Сбросить
              </button>
            )}
          </div>
        </div>

        {/* Dates + search */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-gray-500 w-4 sm:w-auto">С</span>
            <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className="flex-1 sm:flex-none bg-[#0f1117] border border-[#252d42] rounded px-2 py-1.5 text-white" />
            <span className="text-gray-500 w-4 sm:w-auto sm:ml-2">По</span>
            <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className="flex-1 sm:flex-none bg-[#0f1117] border border-[#252d42] rounded px-2 py-1.5 text-white" />
          </div>
          <div className="flex items-center gap-2 flex-1 sm:min-w-[240px]">
            <Search className="w-3.5 h-3.5 text-gray-500 shrink-0" />
            <input type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Поиск: товар / custom_id"
              className="flex-1 bg-[#0f1117] border border-[#252d42] rounded px-2 py-1.5 text-xs text-white min-w-0" />
          </div>
        </div>
      </div>

      {/* List — cards на mobile, table на ≥sm */}
      <div className="bg-[#161b27] border border-[#252d42] rounded-xl overflow-hidden">
        {/* Mobile cards */}
        <div className="sm:hidden">
          {loading && !data && (
            <div className="px-4 py-8 text-center text-gray-500 text-xs">Загрузка…</div>
          )}
          {data && data.items.length === 0 && (
            <div className="px-4 py-8 text-center text-gray-500 text-xs">Нет записей</div>
          )}
          <div className="divide-y divide-[#252d42]">
            {data?.items.map((p) => (
              <button key={p.id} onClick={() => setDetail(p)}
                className="w-full text-left px-4 py-3 hover:bg-white/5 active:bg-white/10 space-y-1.5">
                <div className="flex items-start justify-between gap-2">
                  <span className="font-mono text-xs text-white break-all">{p.product || '—'}</span>
                  <StatusBadge color={p.purchase_status_color} label={p.purchase_status_label} />
                </div>
                {p.custom_id && (
                  <div className="font-mono text-[11px] text-gray-400 break-all">{p.custom_id}</div>
                )}
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-gray-400">{p.active_order?.status_label || '—'}</span>
                  <span className="text-white">
                    {fmtUsd(p.max_price_usd)}
                    {p.exec_price_usd != null && (
                      <span className="text-gray-500"> → {fmtUsd(p.exec_price_usd)}</span>
                    )}
                  </span>
                </div>
                <div className="text-[10px] text-gray-500">{fmtDate(p.created_at)}</div>
              </button>
            ))}
          </div>
        </div>
        {/* Desktop table */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-xs min-w-[760px]">
            <thead className="text-[10px] uppercase tracking-wider text-gray-500 bg-[#0f1117]">
              <tr>
                <th className="text-left px-4 py-2">Дата</th>
                <th className="text-left px-4 py-2">Товар</th>
                <th className="text-left px-4 py-2">Custom ID</th>
                <th className="text-left px-4 py-2">Статус</th>
                <th className="text-left px-4 py-2">Order</th>
                <th className="text-right px-4 py-2">Max</th>
                <th className="text-right px-4 py-2">Exec</th>
              </tr>
            </thead>
            <tbody>
              {loading && !data && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">Загрузка…</td></tr>
              )}
              {data && data.items.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">Нет записей</td></tr>
              )}
              {data?.items.map((p) => (
                <tr key={p.id} onClick={() => setDetail(p)}
                  className="border-t border-[#252d42] hover:bg-white/5 cursor-pointer">
                  <td className="px-4 py-2 text-gray-400">{fmtDate(p.created_at)}</td>
                  <td className="px-4 py-2 font-mono">{p.product || '—'}</td>
                  <td className="px-4 py-2 font-mono text-gray-400">{p.custom_id || '—'}</td>
                  <td className="px-4 py-2"><StatusBadge color={p.purchase_status_color} label={p.purchase_status_label} /></td>
                  <td className="px-4 py-2 text-xs text-gray-400">{p.active_order?.status_label || '—'}</td>
                  <td className="px-4 py-2 text-right">{fmtUsd(p.max_price_usd)}</td>
                  <td className="px-4 py-2 text-right">{fmtUsd(p.exec_price_usd ?? null)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {data && data.total_pages > 1 && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3 border-t border-[#252d42] text-xs">
            <span className="text-gray-500">
              {data.total} запис{data.total % 10 === 1 && data.total % 100 !== 11 ? 'ь' : 'ей'}
            </span>
            <div className="flex items-center justify-between sm:justify-end gap-2">
              <button disabled={page === 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="p-2 hover:bg-white/10 rounded disabled:opacity-30">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-gray-400">Стр. {data.page} из {data.total_pages}</span>
              <button disabled={page >= data.total_pages || loading}
                onClick={() => setPage((p) => p + 1)}
                className="p-2 hover:bg-white/10 rounded disabled:opacity-30">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      <PurchaseDetailDialog purchase={detail} onClose={() => { setDetail(null); setReloadKey((k) => k + 1); }} />
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Stats
// ═════════════════════════════════════════════════════════════════════════════

type Period = '7d' | '30d' | '90d' | 'custom';

function PartnerStats() {
  const [period, setPeriod] = useState<Period>('30d');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [tab, setTab] = useState<'count' | 'volume'>('count');
  const [data, setData] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await partnerApi.getStats(period, dateFrom || undefined, dateTo || undefined);
        if (!cancelled) setData(res);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [period, dateFrom, dateTo]);

  const PIE = ['#ef4444', '#f59e0b', '#3b82f6', '#22c55e', '#a78bfa', '#ec4899'];
  const topData = useMemo(() => {
    if (!data) return [];
    if (tab === 'count') return data.top_items_by_count.map((x) => ({ name: x.product, v: x.count }));
    return data.top_items_by_volume.map((x) => ({ name: x.product, v: Number(x.sum_usd.toFixed(2)) }));
  }, [data, tab]);

  // Filter avg_price_by_day to only points with data for the chart
  const avgPriceData = useMemo(() => {
    if (!data?.avg_price_by_day) return [];
    return data.avg_price_by_day;
  }, [data]);

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white">Статистика</h2>
          <p className="text-sm text-gray-500">Агрегаты из локальной БД</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {(['7d', '30d', '90d'] as Period[]).map((p) => (
            <button key={p} onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 rounded border transition-colors ${
                period === p
                  ? 'bg-blue-500/20 text-blue-300 border-blue-500/50'
                  : 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10'
              }`}>
              {p}
            </button>
          ))}
          <button onClick={() => setPeriod('custom')}
            className={`px-3 py-1.5 rounded border transition-colors ${
              period === 'custom'
                ? 'bg-blue-500/20 text-blue-300 border-blue-500/50'
                : 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10'
            }`}>
            Диапазон
          </button>
          {period === 'custom' && (
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                className="flex-1 sm:flex-none bg-[#0f1117] border border-[#252d42] rounded px-2 py-1.5 text-white" />
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                className="flex-1 sm:flex-none bg-[#0f1117] border border-[#252d42] rounded px-2 py-1.5 text-white" />
            </div>
          )}
        </div>
      </div>

      {loading && !data && <div className="text-center text-gray-500 py-8">Загрузка…</div>}

      {data && (
        <>
          {/* Summary KPI */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard icon={Activity} label="Всего" value={String(data.summary.total_purchases)} accent="blue" />
            <KpiCard icon={DollarSign} label="Потрачено" value={fmtUsd(data.summary.total_spent_usd)} accent="purple" />
            <KpiCard icon={TrendingUp} label="Экономия" value={fmtUsd(data.summary.savings_usd)}
              sub="sum(max - exec)" accent="green" />
            <KpiCard icon={BarChart2} label="Средняя цена" value={fmtUsd(data.summary.avg_exec_price_usd)} accent="blue" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Volume line chart */}
            <div className="bg-[#161b27] border border-[#252d42] rounded-xl p-4">
              <div className="text-sm font-semibold mb-3">Объём покупок по дням</div>
              <div className="h-64">
                <ResponsiveContainer>
                  <LineChart data={data.volume_by_day}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#252d42" />
                    <XAxis dataKey="date" tickFormatter={fmtDay} stroke="#6b7280" fontSize={11} />
                    <YAxis stroke="#6b7280" fontSize={11} />
                    <Tooltip contentStyle={{ background: '#0f1117', border: '1px solid #252d42', color: '#e5e7eb' }}
                      labelFormatter={fmtDay} />
                    <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} dot={false} name="Шт." />
                    <Line type="monotone" dataKey="sum_usd" stroke="#22c55e" strokeWidth={2} dot={false} name="USD" />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Avg exec price line chart */}
            <div className="bg-[#161b27] border border-[#252d42] rounded-xl p-4">
              <div className="text-sm font-semibold mb-3">Средняя цена исполнения по дням</div>
              <div className="h-64">
                {avgPriceData.every(p => p.avg_usd === null) ? (
                  <div className="h-full flex items-center justify-center text-gray-500 text-sm">Нет данных</div>
                ) : (
                  <ResponsiveContainer>
                    <LineChart data={avgPriceData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#252d42" />
                      <XAxis dataKey="date" tickFormatter={fmtDay} stroke="#6b7280" fontSize={11} />
                      <YAxis stroke="#6b7280" fontSize={11} />
                      <Tooltip contentStyle={{ background: '#0f1117', border: '1px solid #252d42', color: '#e5e7eb' }}
                        labelFormatter={fmtDay}
                        formatter={(v) => v != null ? [`$${Number(v).toFixed(2)}`, 'Avg'] : ['—', 'Avg']} />
                      <Line type="monotone" dataKey="avg_usd" stroke="#f59e0b" strokeWidth={2} dot={false}
                        name="Avg USD" connectNulls />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Error pie */}
            <div className="bg-[#161b27] border border-[#252d42] rounded-xl p-4">
              <div className="text-sm font-semibold mb-3">Ошибки по коду</div>
              <div className="h-64">
                {data.error_distribution.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-gray-500 text-sm">
                    Ошибок за период нет
                  </div>
                ) : (
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={data.error_distribution} dataKey="count" nameKey="error_code"
                        cx="50%" cy="50%" outerRadius={75} label={(e) => e.count}>
                        {data.error_distribution.map((_, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ background: '#0f1117', border: '1px solid #252d42', color: '#e5e7eb' }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Top items */}
            <div className="bg-[#161b27] border border-[#252d42] rounded-xl p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                <div className="text-sm font-semibold">Топ-10 предметов</div>
                <div className="flex items-center gap-1 text-xs">
                  <button onClick={() => setTab('count')}
                    className={`flex-1 sm:flex-none px-2 py-1 rounded border ${
                      tab === 'count' ? 'bg-blue-500/20 text-blue-300 border-blue-500/50'
                        : 'bg-white/5 text-gray-400 border-white/10'
                    }`}>По кол-ву</button>
                  <button onClick={() => setTab('volume')}
                    className={`flex-1 sm:flex-none px-2 py-1 rounded border ${
                      tab === 'volume' ? 'bg-blue-500/20 text-blue-300 border-blue-500/50'
                        : 'bg-white/5 text-gray-400 border-white/10'
                    }`}>По объёму</button>
                </div>
              </div>
              {/* Высота больше на mobile — длинные названия предметов в YAxis вертикально занимают много места */}
              <div className="h-72 sm:h-64">
                {topData.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-gray-500 text-sm">Нет данных</div>
                ) : (
                  <ResponsiveContainer>
                    <BarChart data={topData} layout="vertical"
                      margin={{ left: 4, right: 12, top: 4, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#252d42" />
                      <XAxis type="number" stroke="#6b7280" fontSize={11} />
                      {/* width адаптивный: на mobile ~40% контейнера, на desktop фикс 140 */}
                      <YAxis dataKey="name" type="category" stroke="#6b7280" fontSize={10}
                        width={110} tick={{ width: 100 }} interval={0} />
                      <Tooltip contentStyle={{ background: '#0f1117', border: '1px solid #252d42', color: '#e5e7eb' }} />
                      <Bar dataKey="v" fill="#3b82f6" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Profile
// ═════════════════════════════════════════════════════════════════════════════

function PartnerProfile() {
  const { logout, keyLast4 } = usePartner();
  const [data, setData] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [logoutAllBusy, setLogoutAllBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const p = await partnerApi.getProfile();
        setData(p);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleLogout = async () => {
    await logout();
  };

  const handleLogoutAll = async () => {
    setLogoutAllBusy(true);
    try {
      await partnerApi.logoutAll();
      await logout();
    } finally {
      setLogoutAllBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-white">Профиль / Доступ</h2>
        <p className="text-sm text-gray-500">API-ключ и активность сессий</p>
      </div>

      <div className="bg-[#161b27] border border-[#252d42] rounded-xl p-5 space-y-3">
        <Row label="API-ключ">
          <span className="font-mono text-sm text-gray-300">{'•'.repeat(18)}{keyLast4 || data?.key_last4 || '****'}</span>
        </Row>
        <Row label="Первое использование">
          {loading ? '…' : fmtDate(data?.key_first_used_at)}
        </Row>
        <Row label="Последний вход">
          {loading ? '…' : fmtDate(data?.last_login_at)}
        </Row>
        <Row label="IP последнего входа">
          <span className="font-mono text-sm text-gray-300">{loading ? '…' : (data?.last_login_ip || '—')}</span>
        </Row>
      </div>

      {/* Rate limits info */}
      <div className="bg-[#161b27] border border-[#252d42] rounded-xl p-5 space-y-3">
        <div className="text-sm font-semibold text-white mb-2">Лимиты запросов</div>
        <Row label="Auth rate limit">
          <span className="text-gray-300">5 попыток / 15 мин на IP</span>
        </Row>
        <Row label="Кеш баланса">
          <span className="text-gray-300">TTL 15 сек</span>
        </Row>
        <Row label="Кеш dashboard">
          <span className="text-gray-300">TTL 30 сек</span>
        </Row>
        <Row label="Кеш статистики">
          <span className="text-gray-300">TTL 120 сек</span>
        </Row>
        <Row label="Sync покупок">
          <span className="text-gray-300">каждые 45 сек</span>
        </Row>
      </div>

      {/* Audit log — cards на mobile, table на ≥sm */}
      <div className="bg-[#161b27] border border-[#252d42] rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-[#252d42]">
          <div className="text-sm font-semibold">Последняя активность</div>
          <div className="text-xs text-gray-500">10 последних событий авторизации</div>
        </div>
        {/* Mobile cards */}
        <div className="sm:hidden">
          {loading && <div className="px-4 py-6 text-center text-gray-500 text-xs">Загрузка…</div>}
          {!loading && data?.recent_events.length === 0 && (
            <div className="px-4 py-6 text-center text-gray-500 text-xs">Нет событий</div>
          )}
          <div className="divide-y divide-[#252d42]">
            {data?.recent_events.map((e, i) => (
              <div key={i} className="px-4 py-3 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <EventBadge type={e.event_type} />
                  <span className="text-[11px] text-gray-400">{fmtDate(e.created_at)}</span>
                </div>
                <div className="font-mono text-[11px] text-gray-400">IP: {e.ip || '—'}</div>
                <div className="font-mono text-[10px] text-gray-500 break-all">{e.user_agent || '—'}</div>
              </div>
            ))}
          </div>
        </div>
        {/* Desktop table */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-xs min-w-[640px]">
            <thead className="text-[10px] uppercase tracking-wider text-gray-500 bg-[#0f1117]">
              <tr>
                <th className="text-left px-4 py-2">Когда</th>
                <th className="text-left px-4 py-2">Событие</th>
                <th className="text-left px-4 py-2">IP</th>
                <th className="text-left px-4 py-2">User-Agent</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-500">Загрузка…</td></tr>}
              {!loading && data?.recent_events.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-500">Нет событий</td></tr>
              )}
              {data?.recent_events.map((e, i) => (
                <tr key={i} className="border-t border-[#252d42]">
                  <td className="px-4 py-2 text-gray-400">{fmtDate(e.created_at)}</td>
                  <td className="px-4 py-2">
                    <EventBadge type={e.event_type} />
                  </td>
                  <td className="px-4 py-2 font-mono text-gray-400">{e.ip || '—'}</td>
                  <td className="px-4 py-2 font-mono text-gray-500 truncate max-w-[200px]">{e.user_agent || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <Link to="/b2b-api" className="inline-flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300">
          <ExternalLink className="w-4 h-4" /> API-документация
        </Link>
      </div>

      {/* Action buttons — на mobile полная ширина */}
      <div className="flex flex-col sm:flex-row gap-3 pt-2">
        <Button onClick={handleLogout}
          className="bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 w-full sm:w-auto">
          <LogOut className="w-4 h-4 mr-2" /> Выйти
        </Button>
        <Button onClick={handleLogoutAll} disabled={logoutAllBusy}
          className="bg-red-600/20 border border-red-500/30 text-red-400 hover:bg-red-600/30 w-full sm:w-auto">
          <LogOut className="w-4 h-4 mr-2" />
          Выйти со всех устройств
        </Button>
      </div>
    </div>
  );
}

function EventBadge({ type }: { type: string }) {
  const map: Record<string, { label: string; color: string }> = {
    login: { label: 'Вход', color: 'green' },
    login_fail: { label: 'Ошибка входа', color: 'red' },
    logout: { label: 'Выход', color: 'gray' },
    logout_all: { label: 'Выход отовсюду', color: 'yellow' },
  };
  const info = map[type] || { label: type, color: 'gray' };
  return <StatusBadge color={info.color} label={info.label} />;
}

// ═════════════════════════════════════════════════════════════════════════════
// Help
// ═════════════════════════════════════════════════════════════════════════════

function PartnerHelp() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-white">Документация / Помощь</h2>
        <p className="text-sm text-gray-500">Полезные ссылки и контакты</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Link to="/b2b-api"
          className="bg-[#161b27] border border-[#252d42] rounded-xl p-5 hover:border-blue-500/40 transition-colors group">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <ExternalLink className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <div className="font-semibold text-white group-hover:text-blue-400 transition-colors">API-документация</div>
              <div className="text-xs text-gray-500">xPanda P2P API — эндпоинты, параметры, примеры</div>
            </div>
          </div>
        </Link>

        <div className="bg-[#161b27] border border-[#252d42] rounded-xl p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
              <HelpCircle className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <div className="font-semibold text-white">Поддержка</div>
              <div className="text-xs text-gray-500">По вопросам интеграции и работы кабинета</div>
            </div>
          </div>
          <div className="mt-3 text-sm text-gray-400">
            <p>Telegram: <a href="https://t.me/xpanda_support" target="_blank" rel="noopener"
              className="text-blue-400 hover:underline">@xpanda_support</a></p>
          </div>
        </div>
      </div>

      <div className="bg-[#161b27] border border-[#252d42] rounded-xl p-5 space-y-4">
        <div className="font-semibold text-white">Частые вопросы</div>

        <div className="space-y-3 text-sm">
          <div>
            <div className="text-white font-medium mb-1">Как получить API-ключ?</div>
            <p className="text-gray-400">API-ключ выдаётся после заключения партнёрского соглашения. Обратитесь в поддержку для получения ключа.</p>
          </div>
          <div>
            <div className="text-white font-medium mb-1">Почему данные обновляются не мгновенно?</div>
            <p className="text-gray-400">Статусы покупок синхронизируются с Xpanda каждые 45 секунд. Баланс обновляется в реальном времени с кешем 15 сек.</p>
          </div>
          <div>
            <div className="text-white font-medium mb-1">Что означают статусы ошибок?</div>
            <p className="text-gray-400">
              <span className="text-orange-400">Требуется действие</span> — ошибки, которые можно исправить самостоятельно (Trade URL, приватный инвентарь).{' '}
              <span className="text-red-400">Системные</span> — временные проблемы (таймаут, скин недоступен). Повторите покупку.
            </p>
          </div>
          <div>
            <div className="text-white font-medium mb-1">Формула баланса</div>
            <p className="text-gray-400">
              <code className="text-blue-400 bg-blue-500/10 px-1 rounded">available = total - locked</code>. Locked — средства, замороженные под активные реквесты.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Layout (sidebar + header)
// ═════════════════════════════════════════════════════════════════════════════

type Section = 'dashboard' | 'balance' | 'purchases' | 'stats' | 'profile' | 'help';

const NAV: { id: Section; label: string; icon: React.ElementType }[] = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'balance', label: 'Баланс', icon: Wallet },
  { id: 'purchases', label: 'История покупок', icon: History },
  { id: 'stats', label: 'Статистика', icon: BarChart2 },
  { id: 'profile', label: 'Профиль / Доступ', icon: UserCircle },
  { id: 'help', label: 'Помощь', icon: HelpCircle },
];

export function PartnerPage() {
  const { isAuthenticated, isLoading, balance } = usePartner();
  const [section, setSection] = useState<Section>('dashboard');
  const [mobileOpen, setMobileOpen] = useState(false);

  if (isLoading) {
    return <div className="min-h-screen bg-[#0f1117] text-gray-400 flex items-center justify-center">Загрузка…</div>;
  }
  if (!isAuthenticated) {
    return <Navigate to="/partner/login" replace />;
  }

  const SidebarContent = () => (
    <>
      <div className="flex items-center gap-3 px-4 py-5 border-b border-[#252d42]">
        <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center font-black text-white text-sm shadow-lg shadow-blue-500/30">P</div>
        <div>
          <div className="font-bold text-sm">xPanda P2P</div>
          <div className="text-xs text-gray-500">Partner Cabinet</div>
        </div>
      </div>

      {balance && (
        <div className="mx-3 my-3 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
          <div className="text-xs text-gray-500 mb-0.5">Доступно</div>
          <div className="font-bold text-green-400 text-sm">{fmtUsd(balance.available_usd)}</div>
        </div>
      )}

      <nav className="flex-1 py-2">
        {NAV.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => { setSection(id); setMobileOpen(false); }}
            className={`w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
              section === id
                ? 'text-blue-400 border-l-2 border-blue-400 bg-blue-400/10'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}>
            <Icon className="w-4 h-4 shrink-0" />
            {label}
          </button>
        ))}
      </nav>

      <div className="px-4 py-3 border-t border-[#252d42] text-xs text-gray-600">xPanda P2P API © 2025</div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#0f1117] text-[#e2e8f8] flex">
      <nav className="hidden lg:flex w-[252px] min-w-[252px] bg-[#161b27] border-r border-[#252d42] h-screen sticky top-0 flex-col shrink-0 z-50">
        <SidebarContent />
      </nav>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <nav className="absolute left-0 top-0 w-[252px] h-full bg-[#161b27] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#252d42]">
              <span className="font-bold">Меню</span>
              <button onClick={() => setMobileOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto"><SidebarContent /></div>
          </nav>
        </div>
      )}

      <main className="flex-1 min-w-0">
        <div className="lg:hidden flex items-center justify-between px-4 py-3 bg-[#161b27] border-b border-[#252d42]">
          <div className="flex items-center gap-2">
            <button onClick={() => setMobileOpen(true)} className="p-2 hover:bg-white/5 rounded">
              <Menu className="w-5 h-5" />
            </button>
            <span className="font-bold text-sm">Partner Cabinet</span>
          </div>
          {balance && <span className="text-green-400 text-sm font-bold">{fmtUsd(balance.available_usd)}</span>}
        </div>

        <div className="p-4 lg:p-8 max-w-6xl mx-auto">
          {section === 'dashboard' && <PartnerDashboard />}
          {section === 'balance' && <PartnerBalanceSection />}
          {section === 'purchases' && <PartnerPurchases />}
          {section === 'stats' && <PartnerStats />}
          {section === 'profile' && <PartnerProfile />}
          {section === 'help' && <PartnerHelp />}
        </div>
      </main>
    </div>
  );
}
